import * as React from 'react'
import { useQuery } from '@tanstack/react-query'
import { Alert } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { QuantityStepper } from '@/components/ui/quantity-stepper'
import {
  MemberTicketVerification,
  type VerifiedEventBuyer,
} from '@/components/events/member-ticket-verification'
import { postJSON } from '@/lib/api'

export interface TicketType {
  id: number
  name: string
  description: string | null
  price_cents: number
  price: string
  price_label?: string
  unlimited: boolean
  max_per_order: number | null
  available?: boolean
  availability?: string | null
  audience?: 'everyone' | 'members' | 'specific_levels'
}

interface TicketSelectionResponse {
  selection: {
    items: { ticket_type_id: number; quantity: number; subtotal_cents: number }[]
    total_cents: number
    total: string
    currency: string
  }
}

export function PublicTicketPicker({
  event,
  quantities,
  setQuantities,
  onContinue,
  showCta = true,
  memberVerification = null,
  memberSessionVerified = false,
  onMemberVerification,
  onJoinWaitlist,
}: {
  event: { id: number; ticket_types: TicketType[] }
  quantities: Record<number, number>
  setQuantities: React.Dispatch<React.SetStateAction<Record<number, number>>>
  onContinue: (quantities: Record<number, number>) => void
  showCta?: boolean
  memberVerification?: VerifiedEventBuyer | null
  memberSessionVerified?: boolean
  onMemberVerification?: (verification: VerifiedEventBuyer) => void
  onJoinWaitlist?: (ticket: TicketType) => void
}) {
  const selectedTickets = event.ticket_types.filter(
    (ticket) => ticket.available !== false && (quantities[ticket.id] ?? 0) > 0,
  )
  const allSelectedTicketsFree =
    selectedTickets.length > 0 && selectedTickets.every((ticket) => ticket.price_cents === 0)
  const allTicketTypesFree = event.ticket_types.every((ticket) => ticket.price_cents === 0)
  const deferredQuantities = useDebouncedValue(quantities)
  const quoteIsPending = JSON.stringify(quantities) !== JSON.stringify(deferredQuantities)
  const quote = useQuote(
    event.id,
    event.ticket_types,
    deferredQuantities,
    memberVerification?.token ?? null,
  )

  return (
    <>
      {event.ticket_types.some((ticket) => ticket.audience && ticket.audience !== 'everyone') &&
        onMemberVerification && (
          <MemberTicketVerification
            eventId={event.id}
            token={memberVerification?.token ?? null}
            sessionVerified={memberSessionVerified}
            onVerified={onMemberVerification}
          />
        )}
      {event.ticket_types.map((ticket) => (
        <TicketTypeRow
          key={ticket.id}
          ticket={ticket}
          quantity={quantities[ticket.id] ?? 0}
          onQuantityChange={(quantity) => {
            setQuantities((current) => updateQuantity(current, ticket.id, quantity))
          }}
          locked={
            ticket.audience !== undefined &&
            ticket.audience !== 'everyone' &&
            !memberVerification &&
            !memberSessionVerified
          }
          onJoinWaitlist={onJoinWaitlist}
        />
      ))}
      {showCta && (
        <TicketSelectionCta
          selectedTickets={selectedTickets}
          allSelectedTicketsFree={allSelectedTicketsFree}
          allTicketTypesFree={allTicketTypesFree}
          quote={quote}
          quoteIsPending={quoteIsPending}
          onContinue={() => onContinue(quantities)}
        />
      )}
    </>
  )
}

function useQuote(
  eventId: number,
  ticketTypes: TicketType[],
  quantities: Record<number, number>,
  memberVerificationToken: string | null,
) {
  const selectedTickets = ticketTypes.filter((ticket) => (quantities[ticket.id] ?? 0) > 0)
  const singleFreeTicket = selectedTickets.length === 1 && selectedTickets[0]?.price_cents === 0

  return useQuery({
    queryKey: ['public-event-ticket-selection', eventId, quantities],
    queryFn: () =>
      postJSON<TicketSelectionResponse>(`/api/v1/public/events/${eventId}/ticket_selection`, {
        quantities,
        ...(memberVerificationToken ? { member_verification_token: memberVerificationToken } : {}),
      }),
    placeholderData: (previous) => previous,
    enabled: Object.keys(quantities).length > 0 && !singleFreeTicket,
  })
}

function TicketTypeRow({
  ticket,
  quantity,
  onQuantityChange,
  locked,
  onJoinWaitlist,
}: {
  ticket: TicketType
  quantity: number
  onQuantityChange: (quantity: number) => void
  locked: boolean
  onJoinWaitlist?: (ticket: TicketType) => void
}) {
  return (
    <div
      data-testid={`ticket-type-${ticket.id}`}
      data-selected={quantity > 0}
      className={`flex min-h-16 items-center gap-4 border-b border-border px-4 py-3 last:border-b-0 ${quantity > 0 ? 'bg-primary/5' : ''}`}
    >
      <div className="min-w-0 flex-1">
        <p className="text-body-sm" data-testid="ticket-type-name">
          {ticket.name} · <span className="tabular-nums">{ticket.price_label ?? ticket.price}</span>
        </p>
        {ticket.description && (
          <p className="mt-0.5 text-caption text-muted-foreground">{ticket.description}</p>
        )}
        {ticket.availability && ticket.availability !== 'Sold out' ? (
          <p className="mt-0.5 text-caption text-muted-foreground">{ticket.availability}</p>
        ) : null}
        {ticket.max_per_order && (
          <p className="mt-0.5 text-caption text-muted-foreground">
            Maximum {ticket.max_per_order} per Order
          </p>
        )}
      </div>
      {ticket.availability === 'Sold out' ? (
        onJoinWaitlist ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-7 shrink-0 px-2.5 text-caption font-normal"
            onClick={() => onJoinWaitlist(ticket)}
          >
            Join waitlist
          </Button>
        ) : (
          <span className="shrink-0 text-caption text-muted-foreground">Sold out</span>
        )
      ) : locked ? (
        <span className="shrink-0 text-caption text-muted-foreground">Verify to select</span>
      ) : ticket.available !== false ? (
        <QuantityStepper
          label={ticket.name}
          value={quantity}
          max={Math.min(20, ticket.max_per_order ?? 20)}
          onChange={onQuantityChange}
        />
      ) : null}
    </div>
  )
}

function TicketSelectionCta({
  selectedTickets,
  allSelectedTicketsFree,
  allTicketTypesFree,
  quote,
  quoteIsPending,
  onContinue,
}: {
  selectedTickets: TicketType[]
  allSelectedTicketsFree: boolean
  allTicketTypesFree: boolean
  quote: ReturnType<typeof useQuote>
  quoteIsPending: boolean
  onContinue: () => void
}) {
  const hasSelection = selectedTickets.length > 0
  const buttonLabel = checkoutLabel({
    allSelectedTicketsFree,
    allTicketTypesFree,
    hasSelection,
    quote,
    quoteIsPending,
  })

  return (
    <div className="border-t border-border px-4 py-3">
      {quote.isError && (
        <Alert className="mb-3">Unable to update the total. Please try again.</Alert>
      )}
      <Button
        type="button"
        className="h-8 w-full"
        disabled={
          !hasSelection ||
          (quote.isError && !allSelectedTicketsFree) ||
          (quoteIsPending && !allSelectedTicketsFree)
        }
        onClick={onContinue}
      >
        {buttonLabel}
      </Button>
    </div>
  )
}

function checkoutLabel({
  allSelectedTicketsFree,
  allTicketTypesFree,
  hasSelection,
  quote,
  quoteIsPending,
}: {
  allSelectedTicketsFree: boolean
  allTicketTypesFree: boolean
  hasSelection: boolean
  quote: ReturnType<typeof useQuote>
  quoteIsPending: boolean
}) {
  if (allSelectedTicketsFree || allTicketTypesFree) return 'Register'
  if (hasSelection && quote.data && !quote.isPlaceholderData && !quoteIsPending)
    return `Continue · ${quote.data.selection.total}`
  if (hasSelection && (quote.isFetching || quoteIsPending)) return 'Continue · Updating…'

  return 'Continue'
}

function updateQuantity(current: Record<number, number>, ticketId: number, quantity: number) {
  const updated = { ...current, [ticketId]: quantity }
  if (quantity === 0) delete updated[ticketId]
  return updated
}

function useDebouncedValue<T>(value: T, delay = 250) {
  const [debouncedValue, setDebouncedValue] = React.useState(value)

  React.useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedValue(value), delay)
    return () => window.clearTimeout(timer)
  }, [delay, value])

  return debouncedValue
}
